// Passenger details — redesigned for v3 feedback.
//
// Layout:
//   • Sticky step indicator at the top so the user always knows where they
//     are in the search→book chain.
//   • Left column: per-passenger form-cards (Adult 1, Child 1, ...).
//     Conditional Passport block for intl trips; Contact/Address only for
//     the lead pax; GST as an opt-in card at the bottom.
//   • Right column: TripSummaryCard (shared) with a live HoldTimer at top.

import { Fragment, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError, api } from "../lib/api";
import { TripSummaryCard } from "../components/TripSummaryCard";
import { DateField } from "../components/DateField";
import { lookupAirport } from "../lib/airports";
import { useBodyClass } from "../lib/useBodyClass";
import type { Itinerary, Passenger, PaxType } from "../lib/types";

const TITLE_BY_TYPE: Record<PaxType, string[]> = {
  adult: ["Mr", "Ms", "Mrs"],
  child: ["Mstr", "Miss"],
  infant: ["Mstr", "Miss"],
};

function blankPassenger(pax_type: PaxType, is_lead: boolean): Passenger {
  return {
    title: TITLE_BY_TYPE[pax_type][0],
    first_name: "", last_name: "",
    gender: "male",
    pax_type, dob: "", is_lead,
    nationality: "IN",
    email: "", contact_number: "", cell_country_code: "91",
    address_line_one: "", address_line_two: "", city: "",
    country_code: "IN", country_name: "India",
    passport_number: "", passport_expiry: "", passport_issue_date: "",
    frequent_flyer_airline_code: "", frequent_flyer_number: "",
  };
}

function expandPaxList(p: { adult: number; child: number; infant: number }): PaxType[] {
  const out: PaxType[] = [];
  for (let i = 0; i < p.adult; i++) out.push("adult");
  for (let i = 0; i < p.child; i++) out.push("child");
  for (let i = 0; i < p.infant; i++) out.push("infant");
  return out;
}

export function Passengers() {
  useBodyClass("has-bg2");
  const { bookingId } = useParams();
  const nav = useNavigate();
  const qc = useQueryClient();

  const bookingQ = useQuery({
    queryKey: ["booking", bookingId],
    queryFn: () => api.get<Itinerary>(`/v1/flights/booking/${bookingId}/`),
    enabled: !!bookingId,
  });

  const [passengers, setPassengers] = useState<Passenger[] | null>(null);
  const [gstEnabled, setGstEnabled] = useState(false);
  const [acceptDuplicates, setAcceptDuplicates] = useState(false);

  useEffect(() => {
    if (passengers || !bookingQ.data) return;
    const types = expandPaxList(bookingQ.data.pax_count);
    setPassengers(types.map((t, i) => blankPassenger(t, i === 0)));
  }, [bookingQ.data, passengers]);

  const save = useMutation({
    mutationFn: (body: { passengers: Passenger[] }) =>
      api.post<Itinerary>(`/v1/flights/booking/${bookingId}/passengers/`, body),
    onSuccess: (data) => {
      if (data.duplicates_found && !acceptDuplicates) return;
      // Seed React Query cache so the Payment page renders instantly with the
      // fresh booking state — no second roundtrip flash between pages.
      if (bookingId) qc.setQueryData(["booking", bookingId], data);
      // Reprice is folded into the Pay flow now — no intermediate page.
      nav(`/booking/${bookingId}/payment`);
    },
  });

  function updatePax(idx: number, patch: Partial<Passenger>) {
    setPassengers((cur) => cur && cur.map((p, i) => (i === idx ? { ...p, ...patch } : p)));
  }

  function onSubmit() {
    if (!passengers) return;
    const payload = passengers.map((p) => {
      const clean: Record<string, unknown> = { ...p };
      for (const k of ["passport_expiry", "passport_issue_date", "dob"]) {
        if (clean[k] === "") delete clean[k];
      }
      for (const k of ["frequent_flyer_airline_code", "frequent_flyer_number"]) {
        if (clean[k] === "") clean[k] = null;
      }
      if (!gstEnabled) {
        for (const k of ["gst_company_address", "gst_company_contact_number", "gst_company_email", "gst_company_name", "gst_number"]) {
          delete clean[k];
        }
      }
      return clean as unknown as Passenger;
    });
    save.mutate({ passengers: payload });
  }

  const isDomestic = bookingQ.data?.is_domestic ?? true;
  // Whether travel documents are mandatory is the PROVIDER's rule, not the
  // route's: Nuitee rejects a booking without passport details even on a
  // domestic sector. The backend states it on the itinerary; only fall back to
  // the route when an older response didn't carry the field.
  const passportRequired = bookingQ.data?.passport_required ?? !isDomestic;
  const formInvalid = useMemo(() => {
    if (!passengers) return true;
    return passengers.some((p, i) => {
      if (!p.first_name.trim() || !p.last_name.trim() || !p.dob) return true;
      if (i === 0 && (!p.email?.trim() || !p.contact_number?.trim())) return true;
      if (passportRequired && (!p.passport_number?.trim() || !p.passport_expiry)) return true;
      return false;
    });
  }, [passengers, passportRequired]);

  if (bookingQ.isLoading) return <PassengersSkeleton />;
  if (bookingQ.error)     return <div className="app-error">Could not load booking.</div>;
  if (!bookingQ.data || !passengers) return null;

  const booking = bookingQ.data;
  const errMsg = save.error instanceof ApiError ? save.error.message : null;
  const item = booking.itinerary_items[0];
  const firstSeg = item?.legs[0]?.segments[0];
  const lastLeg = item?.legs[item.legs.length - 1];
  const lastSeg = lastLeg?.segments[lastLeg.segments.length - 1];
  const routeLabel = firstSeg && lastSeg
    ? `${lookupAirport(firstSeg.origin.airport_code).city} → ${lookupAirport(lastSeg.destination.airport_code).city}${item.legs.length > 1 ? ` → ${lookupAirport(firstSeg.origin.airport_code).city}` : ""}`
    : "";

  return (
    <>
      <FlowStepper current="passengers" />

      <div className="container">
        <div className="pax-wrap">
          <div>
            <div className="pax-header">
              <h1 className="pax-h">Who's <span className="serif">flying</span>?</h1>
              <p className="pax-sub">
                <b>{routeLabel}</b> · {passengers.length} {passengers.length === 1 ? "traveller" : "travellers"}.
                {passportRequired
                  ? " Passport details are required for this booking — names must match exactly."
                  : " Names must match government-issued ID."}
              </p>
            </div>

            {passengers.map((p, idx) => (
              <PassengerCard
                key={idx}
                index={idx}
                passenger={p}
                passportRequired={passportRequired}
                isDomestic={isDomestic}
                onChange={(patch) => updatePax(idx, patch)}
              />
            ))}

            {passengers[0] && (
              <section className="form-card">
                <div className="form-card-head">
                  <h3>GST details <span className="tag subtle">Optional · all or none</span></h3>
                  <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13.5, color: "var(--ink-2)", fontWeight: 500 }}>
                    <input
                      type="checkbox"
                      style={{ width: 16, height: 16 }}
                      checked={gstEnabled}
                      onChange={(e) => setGstEnabled(e.target.checked)}
                    />
                    I have a GSTIN
                  </label>
                </div>
                {gstEnabled && (
                  <>
                    <div className="form-row cols-2">
                      <Field label="Company name" required>
                        <input value={passengers[0].gst_company_name || ""} onChange={(e) => updatePax(0, { gst_company_name: e.target.value })} />
                      </Field>
                      <Field label="GST number" required>
                        <input value={passengers[0].gst_number || ""} onChange={(e) => updatePax(0, { gst_number: e.target.value.toUpperCase() })} />
                      </Field>
                    </div>
                    <div className="form-row cols-2">
                      <Field label="Company email" required>
                        <input type="email" value={passengers[0].gst_company_email || ""} onChange={(e) => updatePax(0, { gst_company_email: e.target.value })} />
                      </Field>
                      <Field label="Company phone" required>
                        <input value={passengers[0].gst_company_contact_number || ""} onChange={(e) => updatePax(0, { gst_company_contact_number: e.target.value })} />
                      </Field>
                    </div>
                    <div className="form-row">
                      <Field label="Company address" required>
                        <input value={passengers[0].gst_company_address || ""} onChange={(e) => updatePax(0, { gst_company_address: e.target.value })} />
                      </Field>
                    </div>
                  </>
                )}
                <p className="field-help">Partial GST details are rejected by airlines — fill all five fields or skip GST entirely.</p>
              </section>
            )}

            {errMsg && (
              <div className="app-error" style={{ margin: "12px 0" }}>{errMsg}</div>
            )}
          </div>

          <TripSummaryCard
            booking={booking}
            ctaLabel="Save & continue"
            onCta={onSubmit}
            ctaDisabled={formInvalid}
            ctaLoading={save.isPending}
            helpText="Next we'll re-confirm the fare with the airline, then debit your wallet."
          />
        </div>

        {save.data?.duplicates_found && !acceptDuplicates && (
          <DuplicatesModal
            duplicates={save.data.duplicates || []}
            onDismiss={() => save.reset()}
            onAccept={() => { setAcceptDuplicates(true); nav(`/booking/${bookingId}/payment`); }}
          />
        )}
      </div>
    </>
  );
}

// Skeleton shown only when we land on Passengers without a cached booking
// (e.g. direct URL hit, refresh). When the user clicks Continue from Results,
// the cache is pre-populated so this never renders — we land instantly.
function PassengersSkeleton() {
  return (
    <>
      <FlowStepper current="passengers" />
      <div className="container">
        <div className="pax-wrap">
          <div>
            <div className="pax-header">
              <span className="skeleton skeleton-line lg" style={{ width: 280 }} />
              <div style={{ marginTop: 10 }}><span className="skeleton skeleton-line" style={{ width: 460 }} /></div>
            </div>
            {[0, 1].map((i) => (
              <section className="form-card" key={i}>
                <span className="skeleton skeleton-line" style={{ width: 180, marginBottom: 14 }} />
                <div style={{ display: "grid", gridTemplateColumns: "120px 1fr 1fr", gap: 10, marginBottom: 10 }}>
                  <span className="skeleton" style={{ height: 42, borderRadius: 10 }} />
                  <span className="skeleton" style={{ height: 42, borderRadius: 10 }} />
                  <span className="skeleton" style={{ height: 42, borderRadius: 10 }} />
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
                  <span className="skeleton" style={{ height: 42, borderRadius: 10 }} />
                  <span className="skeleton" style={{ height: 42, borderRadius: 10 }} />
                  <span className="skeleton" style={{ height: 42, borderRadius: 10 }} />
                </div>
              </section>
            ))}
          </div>
          <aside className="booking-sidebar">
            <div className="summary-card">
              <span className="skeleton skeleton-line lg" style={{ width: 160 }} />
              <div style={{ marginTop: 16 }}><span className="skeleton" style={{ height: 80, width: "100%", borderRadius: 12 }} /></div>
              <div style={{ marginTop: 14 }}><span className="skeleton" style={{ height: 80, width: "100%", borderRadius: 12 }} /></div>
              <div style={{ marginTop: 18 }}><span className="skeleton" style={{ height: 44, width: "100%", borderRadius: 999 }} /></div>
            </div>
          </aside>
        </div>
      </div>
    </>
  );
}

// ─── flow stepper (used on passengers / reprice / payment) ─────────────────

export function FlowStepper({ current }: { current: "passengers" | "payment" }) {
  // The reprice page is gone (folded into Pay) so the chain is two steps.
  const steps = [
    { key: "passengers", label: "Passenger details" },
    { key: "payment",    label: "Review & pay" },
  ];
  const currentIdx = steps.findIndex((s) => s.key === current);
  return (
    <div className="stepper-wrap">
      <div className="stepper">
        {steps.map((s, i) => (
          // Fragment, not display:contents — the latter is buggy with flex/grid
          // in some browsers and was making the steps render in a single line
          // without the connector.
          <Fragment key={s.key}>
            <div className={`stepper-step ${i < currentIdx ? "done" : i === currentIdx ? "active" : ""}`}>
              <div className="num"><span className="n-digit">{i + 1}</span></div>
              <div className="label">{s.label}</div>
            </div>
            {i < steps.length - 1 && <div className={`stepper-line ${i < currentIdx ? "done" : ""}`} />}
          </Fragment>
        ))}
      </div>
    </div>
  );
}

// ─── passenger form ────────────────────────────────────────────────────────

function PassengerCard(props: {
  index: number; passenger: Passenger; passportRequired: boolean; isDomestic: boolean;
  onChange: (patch: Partial<Passenger>) => void;
}) {
  const { passenger: p } = props;
  const titles = TITLE_BY_TYPE[p.pax_type];
  const headLabel = p.pax_type === "adult" ? `Adult ${props.index + 1}`
    : p.pax_type === "child" ? "Child"
    : "Infant";

  return (
    <>
      <section className="form-card">
        <div className="form-card-head">
          <h3>
            {headLabel}
            {p.is_lead && <span className="lead-badge">Lead passenger</span>}
          </h3>
          <span style={{ fontSize: 12.5, color: "var(--ink-4)" }}>As on {props.passportRequired ? "passport" : "ID"}</span>
        </div>

        <div className="form-row title-name">
          <Field label="Title" required>
            <select value={p.title} onChange={(e) => props.onChange({ title: e.target.value })}>
              {titles.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </Field>
          <Field label="First & middle name" required>
            <input value={p.first_name} onChange={(e) => props.onChange({ first_name: e.target.value })} placeholder="As on passport" />
          </Field>
          <Field label="Last name" required>
            <input value={p.last_name} onChange={(e) => props.onChange({ last_name: e.target.value })} />
          </Field>
        </div>

        <div className="form-row cols-3">
          <DateField
            label="Date of birth"
            required
            value={p.dob || ""}
            onChange={(ymd) => props.onChange({ dob: ymd })}
            maxDate={new Date()}
            minDate={new Date(new Date().getFullYear() - 100, 0, 1)}
            yearFirst
          />
          <Field label="Gender" required>
            <select value={p.gender} onChange={(e) => props.onChange({ gender: e.target.value })}>
              <option value="male">Male</option>
              <option value="female">Female</option>
              <option value="other">Other</option>
            </select>
          </Field>
          <Field label="Nationality" required>
            <input value={p.nationality || ""} maxLength={2} onChange={(e) => props.onChange({ nationality: e.target.value.toUpperCase() })} placeholder="IN" />
          </Field>
        </div>
      </section>

      {props.passportRequired && (
        <section className="form-card">
          <div className="form-card-head">
            {/* Not "international only" any more: Nuitee demands travel
                documents on domestic sectors too, which is exactly why this
                card is gated on passportRequired rather than on the route. */}
            <h3>Passport{props.isDomestic === false && <> <span className="tag sky">International</span></>}</h3>
            <span style={{ fontSize: 12.5, color: "var(--ink-4)" }}>Per fare rules</span>
          </div>
          <div className="form-row cols-3">
            <Field label="Passport number" required>
              <input value={p.passport_number || ""} onChange={(e) => props.onChange({ passport_number: e.target.value.toUpperCase() })} />
            </Field>
            <DateField
              label="Issue date"
              optional
              value={p.passport_issue_date || ""}
              onChange={(ymd) => props.onChange({ passport_issue_date: ymd })}
              maxDate={new Date()}
              minDate={new Date(new Date().getFullYear() - 20, 0, 1)}
              yearFirst
            />
            <DateField
              label="Expiry date"
              required
              value={p.passport_expiry || ""}
              onChange={(ymd) => props.onChange({ passport_expiry: ymd })}
              minDate={new Date()}
              maxDate={new Date(new Date().getFullYear() + 25, 11, 31)}
              yearFirst
            />
          </div>
          <p className="field-help" style={{ marginTop: 10 }}>
            Passport must be valid for at least 6 months past your travel date — that's an airline requirement, not ours.
          </p>
        </section>
      )}

      {p.is_lead && (
        <section className="form-card">
          <div className="form-card-head">
            <h3>Contact &amp; address <span className="tag subtle">Lead only</span></h3>
          </div>
          <div className="form-row cols-2">
            <Field label="Email" required>
              <input type="email" value={p.email || ""} onChange={(e) => props.onChange({ email: e.target.value })} />
            </Field>
            <Field label="Mobile" required>
              <input type="tel" value={p.contact_number || ""} onChange={(e) => props.onChange({ contact_number: e.target.value })} placeholder="9876543210" />
            </Field>
          </div>
          <div className="form-row cols-3">
            <Field label="Address line 1" optional>
              <input value={p.address_line_one || ""} onChange={(e) => props.onChange({ address_line_one: e.target.value })} />
            </Field>
            <Field label="City" optional>
              <input value={p.city || ""} onChange={(e) => props.onChange({ city: e.target.value })} />
            </Field>
            <Field label="Country" optional>
              <input value={p.country_name || ""} onChange={(e) => props.onChange({ country_name: e.target.value })} />
            </Field>
          </div>
        </section>
      )}
    </>
  );
}

function Field({ label, required, optional, children }: {
  label: string; required?: boolean; optional?: boolean; children: React.ReactNode;
}) {
  return (
    <div className="field">
      <label>
        {label}{" "}
        {required && <span className="req">●</span>}
        {optional && <span className="opt">· optional</span>}
      </label>
      {children}
    </div>
  );
}

function DuplicatesModal(props: {
  duplicates: Array<Record<string, unknown>>;
  onDismiss: () => void;
  onAccept: () => void;
}) {
  return (
    <div className="drawer-overlay" onClick={props.onDismiss} style={{ display: "grid", placeItems: "center" }}>
      <div
        role="dialog"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "min(520px, 92vw)", maxHeight: "80vh", overflow: "auto",
          background: "var(--bg)", borderRadius: 16, padding: 26,
          boxShadow: "0 24px 60px rgba(11,18,32,0.25)",
        }}
      >
        <h2 style={{ fontSize: 19, fontWeight: 700, marginBottom: 6 }}>Possible duplicate booking</h2>
        <p style={{ color: "var(--ink-3)", fontSize: 13, marginBottom: 14 }}>
          TravClan flagged matching passenger + sector bookings in the last 7 days. Review before continuing.
        </p>
        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 16 }}>
          {props.duplicates.map((d, i) => (
            <div key={i} style={{ border: "1px solid var(--line)", borderRadius: 10, padding: 12, fontSize: 13 }}>
              <strong>{String(d.pnr || "—")}</strong>
              <span style={{ color: "var(--ink-3)" }}> · {String(d.status || "")} · {String(d.origin_city_code || "")} → {String(d.destination_city_code || "")}</span>
            </div>
          ))}
        </div>
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <button type="button" className="btn btn-ghost btn-pill" onClick={props.onDismiss}>Edit passengers</button>
          <button type="button" className="btn btn-yellow btn-pill" onClick={props.onAccept}>Continue anyway</button>
        </div>
      </div>
    </div>
  );
}
